function _nullishCoalesce(lhs, rhsFn) {
  if (lhs != null) {
    return lhs;
  } else {
    return rhsFn();
  }
}
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import {Temporal} from "temporal-polyfill";
import {getEvents} from "../../services/events/client.ts";
import eventsCalendarSheet from "./events-calendar.css?polyfill=type-css" ;

import eventsSheet from "../../styles/events.css?polyfill=type-css" ;

import themeSheet from "../../styles/theme.css?polyfill=type-css" ;

import {slugifyer, TIME_ZONE} from "../../services/util.ts";
export class EventsCalendarComponent extends HTMLElement {
  #DAYS_IN_WEEK = 7;
  #MAX_CALENDAR_SPACES = 42;
  #events = [];
  #currentMonth;
  #currentMonthData = [];
  constructor() {
    super();
    this.#currentMonth = Temporal.Now.plainDateISO(TIME_ZONE).toPlainYearMonth();
  }
  async connectedCallback() {
    this.#events = await getEvents();
    if (!this.shadowRoot) {
      this.attachShadow({
        mode: "open"
      });
    }
    _optionalChain([this, 'optionalAccess', _ => _.shadowRoot, 'optionalAccess', _2 => _2.adoptedStyleSheets, 'optionalAccess', _3 => _3.push, 'call', _4 => _4(themeSheet, eventsSheet, eventsCalendarSheet)]);
    this.#calculateCurrentMonthData();
    this.render();
  }
  #calculateCurrentMonthData() {
    this.#currentMonthData = [];
    let week = [];
    let monthDateCounter = 1;
    const firstDayOfMonth = this.#currentMonth.toPlainDate({
      day: 1
    });
    const startingDayOfMonth = firstDayOfMonth.dayOfWeek % this.#DAYS_IN_WEEK;
    const eventsByDate = new Map();
    for (const event of this.#events) {
      const eventDate = Temporal.Instant.fromEpochMilliseconds(event.startTime * 1000).toZonedDateTimeISO(TIME_ZONE).toPlainDate();
      if (eventDate.year === this.#currentMonth.year && eventDate.month === this.#currentMonth.month) {
        const dateKey = eventDate.toString();
        const events = _nullishCoalesce(eventsByDate.get(dateKey), () => []);
        events.push(event);
        eventsByDate.set(dateKey, events);
      }
    }
    for (let i = 0, j = this.#MAX_CALENDAR_SPACES; i < j; i += 1) {
      const day = {
        date: null,
        hasEvents: false,
        events: []
      };
      if (i >= startingDayOfMonth && monthDateCounter <= this.#currentMonth.daysInMonth) {
        const date = firstDayOfMonth.add({
          days: monthDateCounter - 1
        });
        day.date = date.day;
        day.events = _nullishCoalesce(eventsByDate.get(date.toString()), () => []);
        day.hasEvents = day.events.length > 0;
        monthDateCounter += 1;
      }
      week.push(day);
      if (week.length === this.#DAYS_IN_WEEK) {
        this.#currentMonthData.push(week);
        week = [];
      }
    }
  }
  #calculatePreviousMonth() {
    this.#currentMonth = this.#currentMonth.subtract({
      months: 1
    });
    this.#calculateCurrentMonthData();
  }
  #calculateNextMonth() {
    this.#currentMonth = this.#currentMonth.add({
      months: 1
    });
    this.#calculateCurrentMonthData();
  }
  #getHeaderText() {
    return this.#currentMonth.toPlainDate({
      day: 1
    }).toLocaleString("en-US", {
      month: "long",
      year: "numeric"
    });
  }
  shiftToPreviousMonth() {
    this.#calculatePreviousMonth();
    this.render();
  }
  shiftToNextMonth() {
    this.#calculateNextMonth();
    this.render();
  }
  render() {
    const html = this.#currentMonthData.map(week => {
      return `
        <div class="as-events-calendar__week">
          ${week.map(day => {
        const dayNotInMonthContent = !day.date ? "<div></div>" : "";
        const dayInMonthContent = day.date && !day.hasEvents ? day.date : "";
        const eventsInDayContent = day.hasEvents ? day.events.map(event => {
          return `
                    <span class="as-events-calendar__day-event">
                      <a class="as-events-calendar__day-event" href="/events/${slugifyer(event.title)}/" title="${event.title}">
                        <i class="fa fa-calendar-check-o"></i>
                      </a>
                    </span>
                  `;
        }) : "";
        return `
                <div class="as-events-calendar__day">
                  <!--day not in month-->
                  ${dayNotInMonthContent}

                  <!--day in month without event-->
                  ${dayInMonthContent}

                  <!--day with event if there's an event-->
                  ${eventsInDayContent}
                </div>
              `;
      })}
        </div>
      `;
    }).join("");
    const headerText = this.#getHeaderText();
        const template = document.createElement('template');
    template.innerHTML = `<div class="as-events-calendar">
        <div class="as-events-calendar__header">
          <button type="button" class="btn btn-default btn-sm as-events-calendar__btn" onclick="this.getRootNode().host.shiftToPreviousMonth(event)" aria-label="goto previous month">
            <i class="fa fa-arrow-left"></i>
          </button>

          <h3 class="as-events-calendar__header-text">
            Event Calendar
            <br><br>
            <span class="as-events-calendar__month">${headerText}</span>
          </h3>

          <button type="button" class="btn btn-default btn-sm as-events-calendar__btn" onclick="this.getRootNode().host.shiftToNextMonth(event)" aria-label="goto next month">
            <i class="fa fa-arrow-right"></i>
          </button>
        </div>

        <div class="as-events-calendar__days">
          <div class="as-events-calendar__day-name">Sun</div>
          <div class="as-events-calendar__day-name">Mon</div>
          <div class="as-events-calendar__day-name">Tue</div>
          <div class="as-events-calendar__day-name">Wed</div>
          <div class="as-events-calendar__day-name">Thu</div>
          <div class="as-events-calendar__day-name">Fri</div>
          <div class="as-events-calendar__day-name">Sat</div>
        </div>

        ${html}
      </div>`;
    if (!this.shadowRoot) {
      this.attachShadow({
        mode: 'open'
      });
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    } else {
      this.shadowRoot.innerHTML = template.innerHTML;
    }

  }
}
customElements.define("as-events-calendar", EventsCalendarComponent);
